import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect } from 'react';

import { Button } from '../button';
import {
  StyledModal,
  StyledModalBackdrop,
  StyledModalBody,
  StyledModalFooter,
  StyledModalHeader,
} from './styled';
import type { ModalBodyProps, ModalFooterProps, ModalHeaderProps, ModalProps } from './types';

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  footer,
  className = '',
  testId,
  styleProps,
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  preventBodyScroll = true,
  strings,
}) => {

  useEffect(() => {
    if (!isOpen || !preventBodyScroll) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, preventBodyScroll]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (closeOnBackdropClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <StyledModalBackdrop
      $styleProps={styleProps}
      onClick={handleBackdropClick}
      data-testid={testId ? `${testId}-backdrop` : undefined}
    >
      <StyledModal
        $styleProps={styleProps}
        className={className}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
        role='dialog'
        aria-modal='true'
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        {(title ?? showCloseButton) && (
          <ModalHeader
            title={title}
            onClose={onClose}
            showCloseButton={showCloseButton}
            strings={strings}
          />
        )}

        <ModalBody>{children}</ModalBody>

        {footer && <ModalFooter>{footer}</ModalFooter>}
      </StyledModal>
    </StyledModalBackdrop>
  );
};

export const ModalHeader: React.FC<ModalHeaderProps> = ({
  title,
  onClose,
  showCloseButton = true,
  className = '',
  children,
  strings,
}) => (
  <StyledModalHeader className={className}>
    {children ?? (
      <>
        {title && <h3 id='modal-title'>{title}</h3>}
        {showCloseButton && onClose && (
          <Button
            onClick={onClose}
            variant='link'
            size='small'
            aria-label={strings?.closeModal}
            type='button'
            styleProps={{
              color: '#90A4BC',
            }}
          >
            <XMarkIcon height={20} width={20} />
          </Button>
        )}
      </>
    )}
  </StyledModalHeader>
);

export const ModalBody: React.FC<ModalBodyProps> = ({ children, className = '' }) => (
  <StyledModalBody className={className}>{children}</StyledModalBody>
);

export const ModalFooter: React.FC<ModalFooterProps> = ({
  children,
  className = '',
  align = 'right',
}) => (
  <StyledModalFooter $align={align} className={className}>
    {children}
  </StyledModalFooter>
);